import { useContext, useMemo } from 'react';

import type { TruncationMode } from '../../../typography/text-ellipsis/TextEllipsis.js';
import { charsFontWidth14pixelsSize500Map } from '../../core/components/text-ellipsis/chars-font-width-map.js';
import { calculateWidth } from '../../core/components/text-ellipsis/truncate-string.js';
import {
  INSIDE_LABEL_PADDING,
  INSIDE_LABEL_WIDTH_RATIO,
} from '../constants.js';
import { SankeyTruncationModeContext } from '../context/SankeyTruncationMode.context.js';
import { useSankeyActiveHighlight } from '../store/selectors.js';
import type {
  SankeyLayoutModel,
  SankeyLayoutNode,
} from '../types/sankey-internal.js';
import {
  insideLabelPositioner,
  insideZoneLabel,
  insideZoneOverlay,
  leftZoneLabel,
  rightZoneLabel,
  sideLabelContainer,
  sideLabelPositioner,
} from './SankeyLabelOverlay.sty.js';
import { SankeyNodeLabel } from './SankeyNodeLabel.js';

type SankeyLabelZone = 'left' | 'inside' | 'right';

type NodeWithGeometry = SankeyLayoutNode & {
  __x0: number;
  __x1: number;
  __y0: number;
  __y1: number;
};

function hasGeometry(node: SankeyLayoutNode): node is NodeWithGeometry {
  return (
    node.__x0 !== undefined &&
    node.__x1 !== undefined &&
    node.__y0 !== undefined &&
    node.__y1 !== undefined
  );
}

interface SankeyLabelOverlayProps {
  zone: SankeyLabelZone;
  layout: SankeyLayoutModel;
  maxWidth?: number;
}

function getVisualColumns(nodes: NodeWithGeometry[]): number[] {
  return [...new Set(nodes.map((n) => n.__x0))].sort((a, b) => a - b);
}

function getInsideLabelMaxWidth(
  node: NodeWithGeometry,
  visualColumns: number[],
): number {
  const nextColX0 = visualColumns.find((x) => x > node.__x0) ?? node.__x1;
  return Math.max(0, (nextColX0 - node.__x1) * INSIDE_LABEL_WIDTH_RATIO);
}

function getMaxLabelWidth(columnNodes: SankeyLayoutNode[]): number {
  let max = 0;
  for (const node of columnNodes) {
    const w = calculateWidth(
      node.label ?? '',
      charsFontWidth14pixelsSize500Map,
    );
    if (w > max) {
      max = w;
    }
  }
  return Math.ceil(max);
}

function renderSideLabels(
  columnNodes: NodeWithGeometry[],
  labelClassName: string,
  truncationMode: TruncationMode,
  isNodeDimmed: (node: SankeyLayoutNode) => boolean,
  maxWidth?: number,
) {
  const measuredWidth = getMaxLabelWidth(columnNodes);
  const effectiveWidth =
    maxWidth !== undefined && maxWidth > 0
      ? Math.min(measuredWidth, maxWidth)
      : measuredWidth;

  return (
    <div className={sideLabelContainer} style={{ width: effectiveWidth }}>
      {columnNodes.map((node) => (
        <div
          key={node.id}
          className={sideLabelPositioner}
          style={{ top: node.__y0, height: node.__y1 - node.__y0 }}
        >
          <SankeyNodeLabel
            label={node.label ?? ''}
            width={effectiveWidth}
            className={labelClassName}
            truncationMode={truncationMode}
            dimmed={isNodeDimmed(node)}
          />
        </div>
      ))}
    </div>
  );
}

export function SankeyLabelOverlay({
  zone,
  layout,
  maxWidth,
}: Readonly<SankeyLabelOverlayProps>) {
  const truncationMode = useContext(SankeyTruncationModeContext);
  const activeSet = useSankeyActiveHighlight();
  const validNodes = layout.nodes.filter(hasGeometry);

  const connectedNodes = useMemo(
    () => (activeSet ? new Set(activeSet.nodes) : undefined),
    [activeSet],
  );

  const isNodeDimmed = (node: SankeyLayoutNode): boolean =>
    connectedNodes !== undefined && !connectedNodes.has(node);

  if (validNodes.length === 0) {
    return null;
  }

  const visualColumns = getVisualColumns(validNodes);
  const leftX0 = visualColumns[0];
  const rightX0 = visualColumns[visualColumns.length - 1];
  const hasMultipleColumns = visualColumns.length > 1;

  if (zone === 'left') {
    const columnNodes = validNodes.filter((n) => n.__x0 === leftX0);
    if (columnNodes.length === 0) {
      return null;
    }
    return renderSideLabels(
      columnNodes,
      leftZoneLabel,
      truncationMode,
      isNodeDimmed,
      maxWidth,
    );
  }

  if (zone === 'right') {
    if (!hasMultipleColumns) {
      return null;
    }
    const lastColumnNodes = validNodes.filter((n) => n.__x0 === rightX0);
    if (lastColumnNodes.length === 0) {
      return null;
    }
    return renderSideLabels(
      lastColumnNodes,
      rightZoneLabel,
      truncationMode,
      isNodeDimmed,
      maxWidth,
    );
  }

  const middleNodes = validNodes.filter(
    (n) => n.__x0 !== leftX0 && (!hasMultipleColumns || n.__x0 !== rightX0),
  );

  if (middleNodes.length === 0) {
    return null;
  }

  return (
    <div className={insideZoneOverlay}>
      {middleNodes.map((node) => {
        const insideWidth = getInsideLabelMaxWidth(node, visualColumns);
        return (
          <div
            key={node.id}
            className={insideLabelPositioner}
            style={{
              top: node.__y0,
              height: node.__y1 - node.__y0,
              left: node.__x1 + INSIDE_LABEL_PADDING,
              maxWidth: insideWidth,
            }}
          >
            <SankeyNodeLabel
              label={node.label ?? ''}
              width={insideWidth}
              className={insideZoneLabel}
              truncationMode={truncationMode}
              dimmed={isNodeDimmed(node)}
            />
          </div>
        );
      })}
    </div>
  );
}

SankeyLabelOverlay['displayName'] = 'SankeyLabelOverlay';
